import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Mail, MessageSquare, Clock } from 'lucide-react';
import { useUnreadMessages } from '@/hooks/useUnreadMessages';
import { formatDistanceToNow } from 'date-fns';

const MessagesNotification: React.FC = () => {
  const navigate = useNavigate();
  const { unreadMessages, loading, markConversationAsRead } = useUnreadMessages();

  const unreadCount = unreadMessages.length;

  const handleMessageClick = async (conversationId: string) => {
    // Mark conversation as read
    await markConversationAsRead(conversationId);
    
    // Navigate to community messages tab with the specific conversation
    navigate('/community?tab=messages', { state: { openConversationId: conversationId } });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="relative h-9 w-9 p-0"
        >
          <Mail className="h-4 w-4" />
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Messages</span>
          <Badge variant="secondary" className="text-xs">
            {unreadCount} unread
          </Badge>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {!loading && unreadMessages.length > 0 ? (
          <>
            {unreadMessages.slice(0, 3).map((message) => (
              <DropdownMenuItem 
                key={message.id} 
                className="p-3 cursor-pointer hover:bg-accent"
                onClick={() => handleMessageClick(message.conversation_id)}
              >
                <div className="flex items-start gap-3 w-full">
                  <Avatar className="w-8 h-8 flex-shrink-0">
                    <AvatarFallback className="text-xs">
                      {message.sender_display_name.substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium truncate">
                        {message.sender_display_name}
                      </p>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {formatDistanceToNow(new Date(message.created_at), { addSuffix: true })}
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground truncate mb-1">
                      {message.content}
                    </p>
                    {message.deal_title && (
                      <p className="text-xs text-blue-600 truncate">
                        Re: {message.deal_title}
                      </p>
                    )}
                  </div>
                </div>
              </DropdownMenuItem>
            ))}
            
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link 
                to="/community?tab=messages" 
                className="text-center py-2 text-sm font-medium text-primary cursor-pointer"
              >
                View All Messages
              </Link>
            </DropdownMenuItem>
          </>
        ) : (
          <div className="p-4 text-center">
            <MessageSquare className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {loading ? 'Loading messages...' : 'No new messages'}
            </p>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default MessagesNotification;